import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    InputBase,
    Paper,
    Typography,
    List,
    ListItemButton,
    Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { docsRoutes } from './DocsRoutes.Component';

const escapeRegExp = (str = '') =>
    str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightText = (text, query) => {
    if (!query) return text;

    const regex = new RegExp(`(${escapeRegExp(query)})`, 'ig');
    const parts = text.split(regex);

    return parts.map((part, index) =>
        regex.test(part) ? (
            <mark
                key={index}
                style={{
                    backgroundColor: 'rgba(255, 215, 0, 0.4)',
                    padding: '0 2px',
                    borderRadius: 2
                }}
            >
                {part}
            </mark>
        ) : (
            part
        )
    );
};

/* ---------------- utils ---------------- */

const flattenRoutes = (routes, parentLabel = '') => {
    let result = [];

    routes.forEach(route => {
        const fullLabel = parentLabel
            ? `${parentLabel} › ${route.label}`
            : route.label;

        if (route.path && route.content) {
            result.push({
                label: fullLabel,
                path: route.path,
                content: route.content
            });
        }

        if (route.children?.length) {
            result = result.concat(
                flattenRoutes(route.children, fullLabel)
            );
        }
    });

    return result;
};

const extractSnippet = (text, query, length = 80) => {
    const lower = text.toLowerCase();
    const index = lower.indexOf(query.toLowerCase());

    if (index === -1) return null;

    const start = Math.max(0, index - length);
    const end = Math.min(text.length, index + query.length + length);

    return `...${text.slice(start, end).replace(/\n/g, ' ')}...`;
};

/* ---------------- component ---------------- */

const DocsSearch = () => {
    const navigate = useNavigate();
    const containerRef = useRef(null);

    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);

    const allDocs = useMemo(() => flattenRoutes(docsRoutes), []);

    const results = useMemo(() => {
        if (!query.trim()) return [];

        return allDocs
            .map(doc => {
                const snippet = extractSnippet(doc.content, query);
                if (!snippet) return null;

                return {
                    ...doc,
                    snippet
                };
            })
            .filter(Boolean)
            .slice(0, 10);
    }, [query, allDocs]);

    useEffect(() => {
        const handleClickOutside = e => {
            if (!containerRef.current?.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                }}
            >
                <SearchIcon fontSize="small" sx={{ mr: 1 }} />
                <InputBase
                    fullWidth
                    placeholder="Search docs…"
                    value={query}
                    onChange={e => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                />
            </Paper>

            {open && results.length > 0 && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        mt: 1,
                        width: '100%',
                        maxHeight: 360,
                        overflowY: 'auto',
                        zIndex: 10
                    }}
                >
                    <List disablePadding>
                        {results.map((item, index) => (
                            <React.Fragment key={item.path}>
                                <ListItemButton
                                    alignItems="flex-start"
                                    onClick={() => {
                                        navigate(`/docs/${item.path}`);
                                        setOpen(false);
                                        setQuery('');
                                    }}
                                >
                                    <Box>
                                        <Typography fontSize={13} fontWeight={600}>
                                            {highlightText(item.label, query)}
                                        </Typography>
                                        <Typography
                                            fontSize={12}
                                            color="text.secondary"
                                        >
                                            {highlightText(item.snippet, query)}
                                        </Typography>
                                    </Box>
                                </ListItemButton>
                                {index !== results.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>
            )}
        </Box>
    );
};

export default DocsSearch;
